/**
 * Buyer Account hub (web) — Phase 5 marketplace.
 *
 * A read-only "home base" for the signed-in buyer: a hero greeting with the
 * buyer's display name (GET /me/profile via the generated me client), a stat
 * grid of account metrics, and quick links into the buyer's transactional
 * surfaces.
 *
 * The metrics come from GET /buyers/me/summary (hand-rolled client in
 * src/api/buyers.ts — orval not regenerated for /buyers, TD-124): orders placed,
 * total spent (micro-ZARP STRING → microToZarp() → <Money>, never Number() for
 * math), and settled-order count. The Watchlist count is CLIENT-ONLY — there is
 * no server watchlist, so we read it from the same localStorage-backed
 * useWatchlist() hook the marketplace / watchlist screens use.
 *
 * Both the summary and profile queries FAIL OPEN: a blip renders the zero /
 * name-less state rather than blocking the hub. The route is authed under _app
 * and role-gated to `buyer` via the sidebar nav spec in _app.tsx; the router
 * registration (central) is returned in the integration report. i18n keys are
 * inlined with English defaults under buyerAccount.*.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Icon,
  Money,
  Skeleton,
  Stack,
  StatCard,
  StatGrid,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { FseApiError } from '../api/client';
import { getBuyerSummary } from '../api/buyers';
import { microToZarp } from '../api/market';
import { meControllerGetProfile } from '../api/generated/me/me';
import { useWatchlist } from '../api/useWatchlist';

function errMsg(err: unknown): string {
  return err instanceof FseApiError ? err.message : String(err);
}

export function BuyerAccountScreen() {
  const { t } = useTranslation();
  const watchlist = useWatchlist();

  // Greeting name — best-effort, shares the ['me-profile'] cache key with the
  // Profile / Home screens. Fails open to a name-less greeting.
  const profileQuery = useQuery({
    queryKey: ['me-profile'],
    queryFn: ({ signal }) => meControllerGetProfile(signal),
    retry: false,
  });

  // Account metrics — buyer-scoped orders aggregate.
  const summaryQuery = useQuery({
    queryKey: ['buyer-summary'],
    queryFn: ({ signal }) => getBuyerSummary(signal),
  });

  const displayName = profileQuery.data?.displayName;
  const summary = summaryQuery.data;

  // Total spent: micro-ZARP STRING → whole-ZARP number for <Money>.
  const totalSpent = summary ? microToZarp(summary.totalSpentZarpMicro) : 0;

  return (
    <Stack spacing={3}>
      {/* Hero */}
      <Box>
        <Text variant="headline-medium" as="h2">
          {displayName
            ? t('buyerAccount.greeting.named', {
                name: displayName,
                defaultValue: 'Welcome, {{name}}',
              })
            : t('buyerAccount.greeting.plain', 'Your account')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('buyerAccount.subtitle', 'Your buying activity at a glance')}
          </Text>
        </Box>
      </Box>

      {/* Metrics */}
      {summaryQuery.isPending ? (
        <StatGrid>
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
        </StatGrid>
      ) : summaryQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => summaryQuery.refetch()}>
              {t('buyerAccount.retry', 'Retry')}
            </Button>
          }
        >
          {t('buyerAccount.loadError', {
            error: errMsg(summaryQuery.error),
            defaultValue: 'Could not load your account summary: {{error}}',
          })}
        </Alert>
      ) : (
        <StatGrid>
          <StatCard
            color="leaf"
            icon="receipt_long"
            label={t('buyerAccount.stats.ordersPlaced', 'Orders placed')}
            value={summary?.ordersCount ?? 0}
          />
          <StatCard
            color="mango"
            icon="payments"
            label={t('buyerAccount.stats.totalSpent', 'Total spent')}
            value={<Money amount={totalSpent} size="md" />}
          />
          <StatCard
            color="blue"
            icon="task_alt"
            label={t('buyerAccount.stats.settledOrders', 'Settled orders')}
            value={summary?.settledCount ?? 0}
          />
          <StatCard
            color="purple"
            icon="star"
            label={t('buyerAccount.stats.watchlist', 'Watchlist')}
            value={watchlist.count}
          />
        </StatGrid>
      )}

      {/* Quick links */}
      <Card>
        <CardContent>
          <Stack spacing={1.5}>
            <Text variant="title-medium" as="h3">
              {t('buyerAccount.quickLinks.title', 'Quick links')}
            </Text>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}
            >
              <QuickLink
                to="/orders"
                icon="receipt_long"
                label={t('buyerAccount.quickLinks.orders', 'My orders')}
              />
              <QuickLink
                to="/my-offers"
                icon="local_offer"
                label={t('buyerAccount.quickLinks.myOffers', 'My offers')}
              />
              <QuickLink
                to="/watchlist"
                icon="star"
                label={t('buyerAccount.quickLinks.watchlist', 'Watchlist')}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

interface QuickLinkProps {
  to: '/orders' | '/my-offers' | '/watchlist';
  icon: string;
  label: string;
}

/** A pill-style quick link to one of the buyer's transactional surfaces. */
function QuickLink({ to, icon, label }: QuickLinkProps) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Button variant="outlined" startIcon={<Icon name={icon} size={18} />}>
        {label}
      </Button>
    </Link>
  );
}
